import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeIndianRupee,
  Headset,
  Puzzle,
  UserCheck,
  ChevronDown,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import Modal from "./Modal";
import { showErrorToast } from "../../utils/toast";

const LEFT_GRADIENT = "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)";
const SUBMIT_GRADIENT = "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)";

const DEFAULT_FEATURES = [
  { icon: BadgeIndianRupee, label: "Flexible Pricing" },
  { icon: Headset, label: "Dedicated Support" },
  { icon: Puzzle, label: "Custom Integrations" },
  { icon: UserCheck, label: "Personalized Onboarding" },
];

const DEFAULT_INDUSTRIES = [
  "Software/IT",
  "Recruitment/Staffing",
  "Healthcare",
  "Finance",
  "Education",
  "Manufacturing",
  "Retail/E-commerce",
  "Marketing/Advertising",
  "Other",
];

const EMPTY_FORM = {
  company_name: "",
  email: "",
  phone: "",
  industry: "",
  location: "",
  message: "",
};

const labelClass = "font-medium text-[#4A4455]";
const labelFont = {
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.14px",
};
const fieldFont = { fontFamily: '"Inter", sans-serif' };

const BASE_INPUT =
  "w-full rounded-[10px] border bg-white px-3.5 py-2 text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] transition-all focus:outline-none focus:ring-2";

const getInputClass = (hasError) =>
  `${BASE_INPUT} ${
    hasError
      ? "border-red-400 focus:border-red-500 focus:ring-red-100"
      : "border-[#E2E8F0] focus:border-[#4F46E5] focus:ring-[#4F46E5]/20"
  }`;

const getPhoneGroupClass = (hasError) =>
  `flex items-stretch overflow-hidden rounded-[10px] border bg-white transition-all focus-within:ring-2 ${
    hasError
      ? "border-red-400 focus-within:border-red-500 focus-within:ring-red-100"
      : "border-[#E2E8F0] focus-within:border-[#4F46E5] focus-within:ring-[#4F46E5]/20"
  }`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (data, agreed) => {
  const errors = {};

  if (!data.company_name.trim()) {
    errors.company_name = "Please enter your company name.";
  }

  if (!data.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = "Enter a valid email address.";
  }

  if (!data.phone.trim()) {
    errors.phone = "Please enter your phone number.";
  } else if (data.phone.length !== 10) {
    errors.phone = "Enter a valid 10-digit phone number.";
  }

  if (!data.industry) {
    errors.industry = "Please select your industry.";
  }

  if (!data.location.trim()) {
    errors.location = "Please enter your location.";
  }

  if (!agreed) {
    errors.agreed = "Please accept the Privacy Policy and Terms to continue.";
  }

  return errors;
};

const FieldError = ({ message }) =>
  message ? (
    <p className="text-[12px] font-medium text-red-500" style={fieldFont}>
      {message}
    </p>
  ) : null;

const Field = ({ label, required, error, children }) => (
  <div className="flex flex-col gap-1">
    <label className={labelClass} style={labelFont}>
      {label}
      {required && <span className="text-[#4F46E5]"> *</span>}
    </label>
    {children}
    <FieldError message={error} />
  </div>
);

const PhoneInput = ({ name, value, error, onChange, fieldFontStyle }) => (
  <div className={getPhoneGroupClass(error)}>
    <span
      className="flex select-none items-center border-r border-[#E2E8F0] bg-[#F8FAFC] px-3 text-[14px] font-medium text-[#475569]"
      style={fieldFontStyle}
    >
      +91
    </span>
    <input
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      name={name}
      value={value}
      onChange={onChange}
      aria-invalid={error ? "true" : undefined}
      placeholder="98765 43210"
      maxLength={10}
      className="w-full bg-transparent px-3.5 py-2 text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none"
      style={fieldFontStyle}
    />
  </div>
);

const CustomPricingModal = ({
  isOpen,
  onClose,
  leftTitle = "Custom Pricing for Every Team",
  leftDescription = "Get a tailored pricing plan based on your business requirements.",
  features = DEFAULT_FEATURES,
  formTitle = "Request Custom Pricing",
  formSubtitle = "Share your requirements and our team will craft a plan for you.",
  submitLabel = "Request a Custom Pricing",
  industries = DEFAULT_INDUSTRIES,
  source = "Landing Page",
  onSubmit,
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  useEffect(() => {
    if (isOpen) return;
    const timer = setTimeout(() => {
      setFormData(EMPTY_FORM);
      setAgreed(false);
      setErrors({});
      setStatus("idle");
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handlePhoneChange = (event) => {
    const { name, value } = event.target;
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, [name]: digits }));
    clearError(name);
  };

  const handleAgreedChange = (event) => {
    setAgreed(event.target.checked);
    clearError("agreed");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm(formData, agreed);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus("submitting");
    try {
      if (onSubmit)
        await onSubmit({ ...formData, phone: `+91${formData.phone}`, source });
      setStatus("success");
    } catch {
      setStatus("idle");
      showErrorToast("Something went wrong. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={formTitle}
      maxWidthClassName="max-w-[1024px]"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col lg:flex-row">
        {/* ── Left brand panel ── */}
        <aside
          className="relative hidden shrink-0 flex-col justify-between overflow-hidden px-10 py-11 lg:flex lg:w-[358px]"
          style={{ background: LEFT_GRADIENT }}
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl"
            aria-hidden
          />
          <div className="relative z-10 flex flex-col gap-5">
            <h2
              className="text-[30px] font-bold leading-[1.15] text-white"
              style={{ fontFamily: '"Rethink Sans", sans-serif' }}
            >
              {leftTitle}
            </h2>
            <p className="text-[15px] leading-[1.6] text-white/85" style={fieldFont}>
              {leftDescription}
            </p>

            <ul className="mt-2 flex flex-col gap-3">
              {features.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 py-2 pl-2.5 pr-4 backdrop-blur-sm"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white">
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                  </span>
                  <span className="text-[13px] font-medium text-white" style={fieldFont}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p
            className="relative z-10 mt-8 text-[12px] leading-relaxed text-white/70"
            style={fieldFont}
          >
            Trusted by 500+ consultancies across India.
          </p>
        </aside>

        {/* ── Right content panel ── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {status === "success" ? (
            <div className="m-auto flex w-full flex-col items-center justify-center gap-4 p-8 text-center sm:p-12">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#4F46E5]/10">
                <CheckCircle2 className="h-9 w-9 text-[#4F46E5]" strokeWidth={2} />
              </div>
              <h3
                className="text-[24px] font-bold text-[#0F172A]"
                style={{ fontFamily: '"Rethink Sans", sans-serif' }}
              >
                Request Received!
              </h3>
              <p
                className="max-w-[380px] text-[15px] leading-relaxed text-[#64748B]"
                style={fieldFont}
              >
                Thanks for reaching out. Our team will review your requirements and
                get back to you with a tailored pricing plan shortly.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-2 inline-flex h-[44px] items-center justify-center rounded-[12px] px-8 text-[14px] font-semibold text-white transition-opacity hover:opacity-95"
                style={{ background: SUBMIT_GRADIENT, ...fieldFont }}
              >
                Done
              </button>
            </div>
          ) : (
            <form
              noValidate
              onSubmit={handleSubmit}
              className="m-auto flex w-full flex-col gap-[13px] px-6 py-8 sm:px-8 md:px-10 lg:px-16 lg:py-10 xl:px-24"
            >
              <div className="flex flex-col gap-1 pr-8">
                <h3
                  className="text-[22px] font-bold text-[#0F172A]"
                  style={{ fontFamily: '"Rethink Sans", sans-serif' }}
                >
                  {formTitle}
                </h3>
                <p className="text-[13.5px] text-[#64748B]" style={fieldFont}>
                  {formSubtitle}
                </p>
              </div>

              <Field label="Company Name" required error={errors.company_name}>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  aria-invalid={errors.company_name ? "true" : undefined}
                  placeholder="Acme Innovations Ltd."
                  className={getInputClass(errors.company_name)}
                  style={fieldFont}
                />
              </Field>

              <div className="grid grid-cols-1 gap-[13px] sm:grid-cols-2">
                <Field label="Email" required error={errors.email}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-invalid={errors.email ? "true" : undefined}
                    placeholder="contact@company.com"
                    className={getInputClass(errors.email)}
                    style={fieldFont}
                  />
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <PhoneInput
                    name="phone"
                    value={formData.phone}
                    error={errors.phone}
                    onChange={handlePhoneChange}
                    fieldFontStyle={fieldFont}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-[13px] sm:grid-cols-2">
                <Field label="Industry" required error={errors.industry}>
                  <div className="relative">
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      aria-invalid={errors.industry ? "true" : undefined}
                      className={`${getInputClass(errors.industry)} appearance-none pr-10 ${
                        formData.industry ? "text-[#0F172A]" : "text-[#94A3B8]"
                      }`}
                      style={fieldFont}
                    >
                      <option value="" disabled>
                        Select industry
                      </option>
                      {industries.map((option) => (
                        <option key={option} value={option} className="text-[#0F172A]">
                          {option}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]"
                      strokeWidth={2}
                    />
                  </div>
                </Field>
                <Field label="Location" required error={errors.location}>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    aria-invalid={errors.location ? "true" : undefined}
                    placeholder="e.g. Kochi"
                    className={getInputClass(errors.location)}
                    style={fieldFont}
                  />
                </Field>
              </div>

              <Field label="Message">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Tell us about your requirements..."
                  className={`${getInputClass(false)} resize-none`}
                  style={fieldFont}
                />
              </Field>

              <div className="flex flex-col gap-1">
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={handleAgreedChange}
                    aria-invalid={errors.agreed ? "true" : undefined}
                    className={`mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded text-[#4F46E5] accent-[#4F46E5] focus:ring-[#4F46E5]/30 ${
                      errors.agreed ? "border-red-400" : "border-[#CBD5E1]"
                    }`}
                  />
                  <span className="text-[13px] leading-relaxed text-[#64748B]" style={fieldFont}>
                    I agree to the{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      className="font-semibold text-[#4F46E5] hover:underline"
                    >
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="font-semibold text-[#4F46E5] hover:underline"
                    >
                      Terms
                    </Link>
                    .
                  </span>
                </label>
                <FieldError message={errors.agreed} />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-0.5 inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-[12px] text-[15px] font-semibold text-white shadow-md shadow-[#4F46E5]/25 transition-all hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: SUBMIT_GRADIENT, ...fieldFont }}
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  submitLabel
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CustomPricingModal;
